import React from 'react';
import { Button, makeStyles } from '@fluentui/react-components';
import { Dismiss12Regular } from '@fluentui/react-icons';
import { QuickSearchTag, ISearchOptionsProps } from './QuickSearchTag';
import { TagPickerItem, adaptItemForQuickSearchTag } from './GenericTagPicker.types';

// Styles for custom tag with dismiss button
const useTagStyles = makeStyles({
    tagWrapper: {
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        backgroundColor: 'transparent',
        borderRadius: '16px',
        overflow: 'hidden',
        // Make wrapper have same background as tag to eliminate color difference
        '& > *:first-child': {
            borderTopRightRadius: '0px !important',
            borderBottomRightRadius: '0px !important'
        }
    },
    dismissButton: {
        marginLeft: '0px',
        marginRight: '0px',
        minWidth: '24px',
        height: '24px', // Match tag height exactly
        padding: '0px',
        border: 'none',
        backgroundColor: '#f3f2f1', // Same as FluentUI tag background
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderTopRightRadius: '16px',
        borderBottomRightRadius: '16px',
        borderTopLeftRadius: '0px',
        borderBottomLeftRadius: '0px',
        transition: 'background-color 0.1s ease',
        '&:hover': {
            backgroundColor: '#edebe9'
        },
        '&:active': {
            backgroundColor: '#e1dfdd'
        }
    },
    // Style to integrate dismiss button seamlessly with the tag
    tagWithDismiss: {
        borderTopRightRadius: '0px !important',
        borderBottomRightRadius: '0px !important',
        paddingRight: '8px',
        marginRight: '0px'
    },
    // Compact dropdown options
    compactDropdown: {
        '& .fui-TagPickerOption': {
            minHeight: '36px !important', // Compact option height
            padding: '4px 8px !important', // Reduced padding
        }
    }
});

interface TagWithDismissProps<T extends TagPickerItem> {
    item: T;
    isMultiSelect: boolean;
    disabled?: boolean;
    hideCoin?: boolean;
    isContact?: boolean;
    customAvatar?: (option: ISearchOptionsProps) => React.JSX.Element;
    onDismiss: (itemId: string) => void;
}

export const TagWithDismiss = <T extends TagPickerItem>({
    item,
    isMultiSelect,
    disabled = false,
    hideCoin = false,
    isContact = false,
    customAvatar,
    onDismiss
}: TagWithDismissProps<T>) => {
    const tagStyles = useTagStyles();

    return (
        <div key={item.key ?? item.id} className={tagStyles.tagWrapper}>
            <QuickSearchTag
                option={adaptItemForQuickSearchTag(item)}
                hideCoin={hideCoin}
                disabled={disabled}
                showSecondaryTextInSelection={false}
                customAvatar={customAvatar}
                size="small"
                maxTextLength={isMultiSelect ? 35 : 45} // Allow longer text for case descriptions
                isContact={isContact} // Pass through contact flag for avatar styling
                withDismissButton={!disabled} // Indicate if dismiss button will be present
            />
            {!disabled && (
                <Button
                    className={tagStyles.dismissButton}
                    icon={<Dismiss12Regular />}
                    size="small"
                    appearance="transparent"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDismiss(item.id.toString());
                    }}
                    aria-label={`Remove ${item.text || item.displayText}`}
                    title={`Remove ${item.text || item.displayText}`}
                />
            )}
        </div>
    );
};

// Export the compact dropdown style for use in GenericTagPicker
export const useCompactDropdownStyles = () => {
    const tagStyles = useTagStyles();
    return tagStyles.compactDropdown;
};

export default TagWithDismiss;
